import React, { createContext, useState, useContext } from "react";
import { GetCartCount } from "../api/cartService";
import { GetWishlistCount } from "../api/wishlistService";
import { GetEnquiryCount, insertEnquiry } from "../api/enquiryService";
import { StyleGetCartCount } from "../api/styleJewelleryService";

const CountContext = createContext();

export const useCount = () => useContext(CountContext);

export const CountProvider = ({ children }) => {
    const [cartCount, setCartCount] = useState(0);
    const [wishlistCount, setWishlistCount] = useState(0);
    const [enquiryCount, setEnquiryCount] = useState(0);

    // Global Enquiry State
    const [enquiryItems, setEnquiryItems] = useState(() => {
        const saved = localStorage.getItem("enquiry_basket");
        return saved ? JSON.parse(saved) : [];
    });
    const [enquiryRemark, setEnquiryRemark] = useState(() => {
        return localStorage.getItem("enquiry_remark") || "";
    });
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false);
    const [submittingEnquiry, setSubmittingEnquiry] = useState(false);

    // Persistence Effect
    React.useEffect(() => {
        localStorage.setItem("enquiry_basket", JSON.stringify(enquiryItems));
        localStorage.setItem("enquiry_remark", enquiryRemark);
    }, [enquiryItems, enquiryRemark]);

    // Global Success Modal State
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [successType, setSuccessType] = useState("");
    const [successTitle, setSuccessTitle] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const fetchCartCount = async (SelectedCustomer_ID) => {
        try {
            const res = await GetCartCount({
                Party_ID: SelectedCustomer_ID,
                StockCategory: "STOCK",
                BasketType: "STOCK"
            });
            const cnt = Number(res?.ROW?.[0]?.CNT || res?.CartCount?.[0]?.CNT || 0);
            setCartCount(Number(cnt || 0));
        } catch (err) {
            console.error("fetchCartCount error:", err);
            setCartCount(0);
        }
    };

    const fetchStyleCartCount = async (SelectedCustomer_ID) => {
        try {
            const res = await StyleGetCartCount({
                Party_ID: SelectedCustomer_ID,
                StockCategory: "STYLE",
                BasketType: "ORDER"
            });
            const cnt = Number(res?.ROW?.[0]?.CNT || res?.CartCount?.[0]?.CNT || 0);
            setCartCount(Number(cnt || 0));
        } catch (err) {
            console.error("fetchStyleCartCount error:", err);
            setCartCount(0);
        }
    };

    const fetchWishlistCount = async (SelectedCustomer_ID) => {
        try {
            const res = await GetWishlistCount({
                Party_ID: SelectedCustomer_ID,
                StockCategory: "STYLE",
                BasketType: "WISHLIST"
            });
            const cnt = Number(res?.ROW?.[0]?.CNT || res?.WishlistCount?.[0]?.CNT || 0);
            setWishlistCount(Number(cnt || 0));
        } catch (err) {
            console.error("fetchWishlistCount error:", err);
            setWishlistCount(0);
        }
    };

    const fetchEnquiryCount = async (SelectedCustomer_ID) => {
        try {
            const res = await GetEnquiryCount({
                Party_ID: SelectedCustomer_ID
            });
            const cnt = res?.ROW?.[0]?.EnquiryCount ?? 0;
            setEnquiryCount(Number(cnt));
        } catch (err) {
            console.error("fetchEnquiryCount error:", err);
            setEnquiryCount(0);
        }
    };

    const addToEnquiry = (product) => {
        setEnquiryItems(prev => {
            const exists = prev.some(p => p.Style_ID === product.Style_ID);
            if (exists) return prev;
            return [
                ...prev,
                {
                    Style_ID: product.Style_ID,
                    StyleNo: product.StyleNo,
                    JewelCode: product.JewelCode,
                    Shortdescription: product.Shortdescription,
                    MetalQuality: product.MetalQuality,
                    MetalTone: product.MetalTone,
                    TotalNetWt: product.TotalNetWt,
                    Cart_ID: null,
                    CUSTOMERNAME: product.CustomerName || product.CUSTOMERNAME,
                    Quantity: 1
                }
            ];
        });
        setIsEnquiryOpen(true);
    };

    const removeFromEnquiry = (productId) => {
        setEnquiryItems(prev => prev.filter(p => p.Style_ID !== productId));
    };

    const submitEnquiry = async (partyId, markInEnquiryFn) => {
        if (!partyId || partyId === 0 || partyId === "0") {
            return { success: false, message: "Please select a specific customer to submit enquiry." };
        }
        if (!enquiryItems.length) return;
        setSubmittingEnquiry(true);

        const payload = {
            Enquiry_ID: "0",
            EnquiryNo: "",
            Status: "PENDING",
            StatusComment: enquiryRemark,
            StyleCount: String(enquiryItems.length),
            Party_ID: partyId,
            OrderCategory: "ENQUIRY",
            Styles: enquiryItems.map(x => ({
                Style_ID: x.Style_ID,
                StyleNo: x.StyleNo,
                JewelCode: x.JewelCode,
                Cart_ID: x.Cart_ID != null ? String(x.Cart_ID) : ""
            }))
        };

        try {
            const res = await insertEnquiry(payload);
            if (res?.ZMESSAGE?.[0]?.RETURNCODE === "0") {
                return { success: false, message: res?.ZMESSAGE?.[0]?.MESSAGE || "Failed to create enquiry" };
            }

            const messageObj = res?.EnquiryData?.[0];
            const rawMsg = messageObj?.ReturnMessage || "";
            const normalizedMsg = rawMsg.replace(/\s+/g, " ").trim();
            const enquiryNoMatch = normalizedMsg.match(/Enquiry\s*No\s*=\s*([A-Z0-9/]+)/i);
            const enquiryNo = enquiryNoMatch ? enquiryNoMatch[1] : "Enquiry Created";
            const cleanMessage = normalizedMsg
                .replace(/^\(\d+\)\s*/g, "")
                .replace(/\(Enquiry\s*No\s*=.*?\)/i, "")
                .trim();

            const styleIds = enquiryItems.map(x => x.Style_ID);
            if (markInEnquiryFn) markInEnquiryFn(styleIds);

            setEnquiryItems([]);
            setEnquiryRemark("");
            setIsEnquiryOpen(false);
            fetchEnquiryCount(partyId);

            // Set global success data
            setSuccessTitle(enquiryNo);
            setSuccessMessage(cleanMessage);
            setSuccessType("enquiry");
            setIsSuccessModalOpen(true);

            return { success: true };
        } catch (err) {
            console.error("Exception inserting enquiry:", err);
            return { success: false, message: "Something went wrong" };
        } finally {
            setSubmittingEnquiry(false);
        }
    };

    return (
        <CountContext.Provider
            value={{
                cartCount,
                setCartCount,
                fetchCartCount,
                fetchStyleCartCount,
                enquiryCount,
                setEnquiryCount,
                fetchEnquiryCount,
                enquiryItems,
                setEnquiryItems,
                enquiryRemark,
                setEnquiryRemark,
                isEnquiryOpen,
                setIsEnquiryOpen,
                submittingEnquiry,
                addToEnquiry,
                removeFromEnquiry,
                submitEnquiry,
                wishlistCount,
                setWishlistCount,
                fetchWishlistCount,
                isSuccessModalOpen,
                setIsSuccessModalOpen,
                successTitle,
                successMessage,
                successType
            }}
        >
            {children}
        </CountContext.Provider>
    );
};

export default CountContext;
